import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Tooltip from "@edaz/edazkit/Tooltip";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import PayGateRefundsViewModel from "viewModels/finance/refundApprovals/payGateRefunds/payGateRefundsViewModel";
import type PayGateRefundResponse from "entities/payGateRefundResponse";
import { formatAmount, formatDate } from "utils/localizations";
import PayGateRefundsFilters from "./payGateRefundsFilters";
import type { ListColumnDefinition } from "../../../common/list";
import ListView from "../../../common/list";
import { ApprovalColumns } from "../../../common/approvalColumns";
import { emptyValue } from "utils/emptyValue";

export const refundPaymentProcessingInfo = (translate: (code: string) => string) => (
  <Tooltip id="refund-payment-processing" trigger={["hover", "focus"]} tooltip={translate("refundPaymentProcessing")}>
    <span>
      <Icon icon={IconType.Processing} width={30} height={30} />
    </span>
  </Tooltip>
);

const PayGateRefundsView: ViewComponent<PayGateRefundsViewModel> = observer(({ vm }) => {
  const columns: ListColumnDefinition<PayGateRefundResponse>[] = [
    {
      title: <Check custom target={vm as { allItems?: boolean }} property="allItems" threeState={true} />,
      valueFormatter: props => {
        if (props.item.isSelectable) {
          return <CollectionCheck custom target={vm} property="selectedItems" disabled={false} value={props.item} />;
        }
      },
    },
    {
      valueFormatter: ({ item }) => (item.originalPayment ? "" : refundPaymentProcessingInfo(vm.translateModuleGeneral)),
    },
    {
      title: vm.translateModelAttribute("licensePlate", "licensePlate"),
      valueFormatter: ({ item }) =>
        item.charge ? `${vm.countryCode(item.charge.vehicle.countryId)} - ${item.charge.vehicle.licensePlate}` : emptyValue,
      cellClassName: "font-weight-bold",
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "authorizedAt"),
      valueFormatter: ({ item }) => (item.originalPayment ? formatDate(item.originalPayment.authorizedAt) : emptyValue),
      dateProperty: true,
    },
    {
      title: vm.translateModelAttribute("payGateRefund", "payGatePaymentIdent"),
      valueFormatter: ({ item }) => item.payGatePaymentIdent ?? emptyValue,
    },
    {
      title: vm.translateModelAttribute("refundOriginalPayGatePayment", "variableSymbol"),
      valueFormatter: ({ item }) => item.originalPayment?.variableSymbol ?? emptyValue,
      numberProperty: true,
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "paidAmount"),
      valueFormatter: ({ item }) => (item.originalPayment ? formatAmount(item.originalPayment.paidAmount) : emptyValue),
      numberProperty: true,
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "invoiceNumber"),
      valueFormatter: ({ item }) => (item.originalPayment ? item.originalPayment.invoiceNumber : emptyValue),
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "arn"),
      valueFormatter: ({ item }) => (item.originalPayment ? item.originalPayment.arn : emptyValue),
    },
    {
      title: vm.translateModelAttribute("payGateRefundPayment", "terminalId"),
      valueFormatter: ({ item }) => (item.originalPayment ? item.originalPayment.terminalId : emptyValue),
    },
    {
      title: vm.translateModelAttribute("refund", "officialRecordId"),
      valueFormatter: ({ item }) => (item.officialRecordId ? item.officialRecordId : emptyValue),
    },
    {
      title: vm.translateModelAttribute("refund", "refundPaymentTemplateId"),
      valueFormatter: ({ item }) => vm.translateRefundReason(item.refundPaymentTemplateId),
    },
    {
      title: vm.translateModelAttribute("refund", "refundAmount"),
      valueFormatter: ({ item }) => formatAmount(item.refundAmount),
      numberProperty: true,
    },
    ...ApprovalColumns<PayGateRefundResponse, PayGateRefundsViewModel>(vm),
  ];

  return (
    <>
      <PayGateRefundsFilters vm={vm} />
      <ListView columns={columns} vm={vm} showDetailWithList={true} hideTitle={true} hideButtons={true} />
    </>
  );
});

registerView(PayGateRefundsView, PayGateRefundsViewModel);
